// Die Sammlungen in der Oberflaeche (ST-SEA-002 Plan F3, Schnitt 3a; ADR-0065).
// Abrufe ueber den Web-Kern (command-client.ts), Saetze je Grund und die Worte
// fuer Zahl, Umfang und Auftrag.
//
// KEIN ORAKEL (ADR-0065 Ziffer 4): Jede Zahl ist die sichtbare Gesamtzahl, die
// der Server nennt. Die Oberflaeche rechnet nie selbst ("n hinzugefuegt",
// "x geladen") und nennt nie Mitglieder, die sie nicht zeigt.

import type { PlainMessage } from './access.ts';
import { readResource, sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';
import { mengeWort, type HausArt } from './haus-art.ts';
import { filterBody, type SearchFilter } from './parties.ts';

/** Hoechstlaenge des Namens -- Abschrift von MAXIMUM_COLLECTION_NAME_LENGTH der API, die Probe liest beide. */
export const MAXIMUM_COLLECTION_NAME_LENGTH = 120;
/** Ab hier zeigt das Namensfeld einen Zaehler. */
export const NAME_COUNTER_FROM = 100;
/** Takt der Abfrage eines Fuellauftrags -- derselbe wie beim Export (POLL_MILLISECONDS), die Probe vergleicht. */
export const FILL_POLL_MILLISECONDS = 3000;
/** Kennungen je Aufruf der Mitgliederroute -- ihre technische Blockgroesse, keine Grenze der Auswahl. */
export const MEMBER_BLOCK_SIZE = 250;
/** Wie viele Zahlen der Uebersicht gleichzeitig geladen werden. */
export const COUNT_CONCURRENCY = 4;

export interface CollectionSummary {
  readonly collectionId: string;
  readonly collectionName: string;
  readonly createdAt: string;
  readonly lockVersion: number;
  /** Nur aus der Einzelroute und den Schreibwegen; die Liste traegt keine Zahl. */
  readonly memberCount?: number | undefined;
}

export interface CollectionMember {
  readonly partyId: string;
  readonly kind: string;
  readonly lifecycleStatus: string;
  readonly displayName: string;
}

export type FillStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface FillSummary {
  readonly fillId: string;
  readonly collectionId: string;
  readonly status: FillStatus;
  readonly failureReason?: string | undefined;
  readonly memberCount?: number | undefined;
}

// --- Saetze je Grund ----------------------------------------------------------

const msg = (headline: string, explanation: string, nextStep: string, retryable = false): PlainMessage =>
  Object.freeze({ headline, explanation, nextStep, retryable });

/** Fremd, beendet, unbekannt: von aussen dasselbe 404 (CM-SEC-003). */
export const COLLECTION_GONE = msg(
  'Diese Sammlung gibt es nicht mehr.',
  'Sie wurde beendet, oder die Adresse stimmt nicht. Es wurde nichts hinzugefügt.',
  'Wähl eine andere Sammlung oder leg eine neue an.',
);

export const NAME_TAKEN = msg('', 'Du hast schon eine Sammlung mit diesem Namen.', 'Wähl einen anderen Namen oder nimm die vorhandene Sammlung.');

/** "Für so viele Verlage …" nach der Art der eigenen Haeuser (M7); ohne Arten "Gesellschaften der Gruppe". */
export function fillTooManyContexts(arten?: readonly (HausArt | null | undefined)[]): PlainMessage {
  return msg(`Für so viele ${mengeWort(arten)} auf einmal geht das Übernehmen noch nicht.`, 'Es wurde nichts übernommen.', 'Wende dich an die LIAISON-Administration.');
}
/** Die feste Fassung, an der der Dialog die Meldung wiedererkennt und nach Art ersetzt. */
export const FILL_TOO_MANY_CONTEXTS = fillTooManyContexts();

const REASON_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  collection_name_taken: NAME_TAKEN,
  collection_fill_running: msg(
    'In diese Sammlung werden gerade Treffer übernommen.',
    'Ein zweiter Auftrag kann erst danach starten.',
    'Warte, bis das fertig ist, oder wähl eine andere Sammlung.',
  ),
  collection_fill_too_many_contexts: FILL_TOO_MANY_CONTEXTS,
  client_request_reused: msg(
    'Diese Anfrage wurde schon mit anderen Angaben gestellt.',
    'Es wurde nichts angelegt.',
    'Versuch es noch einmal.',
  ),
});

/** Die Grundcodes, die diese Oberflaeche selbst erklaert. */
export const EXPLAINED_REASON_CODES: readonly string[] = Object.freeze(Object.keys(REASON_MESSAGES));

const MESSAGES: CommandMessages = Object.freeze({
  notFound: COLLECTION_GONE,
  conflictFallback: msg('Die Sammlung hat sich gerade geändert.', 'Es wurde nichts hinzugefügt.', 'Versuch es noch einmal.'),
  conflicts: REASON_MESSAGES,
});

/**
 * Der Hinweis am Namen (TM-C10): der Name ueberlebt Loeschung und
 * Anonymisierung einer Party, also gehoeren keine Personen hinein. Ab
 * NAME_COUNTER_FROM folgt der Zaehler als zweiter Satz.
 */
export const NAME_PERSON_HINT = 'Nenne darin keine Personen, also nicht ‚Absagen Müller‘.';
export function nameHint(laenge: number): string {
  return laenge >= NAME_COUNTER_FROM ? `${NAME_PERSON_HINT} ${laenge} von ${MAXIMUM_COLLECTION_NAME_LENGTH} Zeichen.` : NAME_PERSON_HINT;
}

/** Der Satz am Feld beim Umbenennen: dort gibt es keine vorhandene Sammlung zu nehmen. */
export const RENAME_NAME_TAKEN = 'Du hast schon eine Sammlung mit diesem Namen. Wähl einen anderen Namen.';

/**
 * Ein anderer Tab war schneller (lockVersion); die Ansicht laedt danach neu.
 * Scheitert das Neuladen, sagt der Satz das, statt "neu geladen" zu behaupten.
 */
export function outdatedMessage(was: 'umbenennen' | 'beenden', neuGeladen = true): PlainMessage {
  return msg(
    'Die Sammlung wurde inzwischen geändert.',
    was === 'umbenennen' ? 'Dein neuer Name wurde nicht gespeichert.' : 'Sie wurde nicht beendet.',
    neuGeladen ? 'Sie ist neu geladen; versuch es noch einmal.' : 'Der neue Stand ließ sich gerade nicht laden. Lade die Seite neu und versuch es dann noch einmal.',
  );
}

/** Die Statussaetze der Ansicht und der Uebersicht. */
export function renamedSentence(name: string): string {
  return `Umbenannt in ‚${name}‘.`;
}
export function endedSentence(name: string): string {
  return `‚${name}‘ ist beendet.`;
}

/** Die Zahl danach in Worten, mit "ist" bei einem Kontakt. */
function jetztSatz(name: string, count: number): string {
  const worte = count === 0 ? 'keine Kontakte' : count === 1 ? '1 Kontakt' : `${count.toLocaleString('de-DE')} Kontakte`;
  return `In ‚${name}‘ ${count === 1 ? 'ist' : 'sind'} jetzt ${worte}.`;
}

/**
 * Nach dem Entfernen. `entfernt` ist die eigene Auswahl, kein Ergebnis des
 * Servers, und deshalb kein Orakel (ADR-0065 Ziffer 4); die Zahl danach ist
 * die sichtbare aus der letzten Antwort.
 */
export function removedSentence(name: string, entfernt: number, count: number): string {
  const wer = entfernt === 1 ? '1 Kontakt' : `${entfernt.toLocaleString('de-DE')} Kontakte`;
  return `${wer} aus ‚${name}‘ entfernt. ${jetztSatz(name, count)}`;
}

/** Nach "Rückgängig". */
export function restoredSentence(name: string, count: number): string {
  return `Wieder in ‚${name}‘. ${jetztSatz(name, count)}`;
}

export const REMOVE_FAILED = msg('Die Kontakte wurden nicht entfernt.', 'Deine Auswahl ist erhalten.', 'Versuch es noch einmal.', true);
export const PARTLY_REMOVED = msg(
  'Nicht alle ausgewählten Kontakte wurden entfernt.',
  'Was schon entfernt ist, bleibt entfernt. Deine Auswahl ist erhalten.',
  'Versuch es noch einmal.',
  true,
);
export const RESTORE_FAILED = msg('Die Kontakte wurden nicht wieder hinzugefügt.', 'Sie bleiben aus der Sammlung entfernt.', 'Versuch es mit „Rückgängig“ noch einmal.', true);
export const PARTLY_RESTORED = msg(
  'Nicht alle Kontakte wurden wieder hinzugefügt.',
  'Was schon wieder drin ist, bleibt drin.',
  'Versuch es mit „Rückgängig“ noch einmal.',
  true,
);
export const REMOVE_PROGRESS_LABEL = 'Ausgewählte Kontakte werden entfernt';
export const RESTORE_PROGRESS_LABEL = 'Kontakte werden wieder hinzugefügt';

/** "die 2 Kontakte" bzw. "der Kontakt" -- die eigene Auswahl, kein Orakel. */
function welche(anzahl: number): { readonly wer: string; readonly verb: string } {
  return anzahl === 1 ? { wer: 'der Kontakt', verb: 'wurde' } : { wer: `die ${anzahl.toLocaleString('de-DE')} Kontakte`, verb: 'wurden' };
}

/**
 * Ausgang unklar nach einem Netzabbruch (der Server hat vielleicht schon
 * entfernt). Zweiter Satz je nachdem, ob das stille Neuladen gelang.
 */
export function removeUnknownSentence(anzahl: number, neuGeladen: boolean): string {
  const { wer, verb } = welche(anzahl);
  return neuGeladen
    ? `Ob ${wer} entfernt ${verb}, ist offen. Prüfe die neu geladene Liste. Deine Auswahl ist erhalten.`
    : // Nach dem Neuladen der Seite ist die Auswahl weg; der Satz verspricht sie deshalb nicht.
      `Ob ${wer} entfernt ${verb}, ist offen. Lade die Seite neu und prüfe die Liste.`;
}

/** Dasselbe fuer "Rückgängig"; der Knopf bleibt, ein zweites Hinzufuegen ist idempotent. */
export function restoreUnknownSentence(name: string, anzahl: number, neuGeladen: boolean): string {
  const wer = anzahl === 1 ? 'der Kontakt' : `die ${anzahl.toLocaleString('de-DE')} Kontakte`;
  const ist = anzahl === 1 ? 'ist' : 'sind';
  return `Ob ${wer} wieder in ‚${name}‘ ${ist}, ist offen. ${neuGeladen ? 'Prüfe die neu geladene Liste.' : 'Lade die Seite neu und prüfe die Liste.'}`;
}

const NAME_ERRORS: Readonly<Record<string, string>> = Object.freeze({
  field_required: 'Gib der Sammlung einen Namen.',
  field_too_long: `Der Name ist zu lang (höchstens ${MAXIMUM_COLLECTION_NAME_LENGTH} Zeichen).`,
  field_malformed: 'Der Name enthält unsichtbare Zeichen oder Steuerzeichen. Gib ihn neu ein.',
});

/** Der Satz am Namensfeld zu einer Feldablehnung des Servers. */
export function nameErrorSentence(reasonCode: string): string {
  return NAME_ERRORS[reasonCode] ?? NAME_ERRORS['field_malformed']!;
}

/**
 * Wie der Browser vor dem Senden prueft -- dieselben Grenzen wie der Server,
 * damit ein leerer Name keinen Aufruf kostet. Gezaehlt in Codepunkten wie dort.
 */
export function nameProblem(name: string): string | undefined {
  const trimmed = name.normalize('NFC').trim();
  if (trimmed.length === 0) return NAME_ERRORS['field_required'];
  if ([...trimmed].length > MAXIMUM_COLLECTION_NAME_LENGTH) return NAME_ERRORS['field_too_long'];
  return undefined;
}

const FILL_FAILED = 'Die Treffer wurden nicht übernommen.';

const FILL_FAILURES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  request_stale: msg(FILL_FAILED, 'Der Auftrag hat zu lange gewartet.', 'Starte ihn noch einmal.', true),
  collection_ended: msg(FILL_FAILED, 'Die Sammlung wurde inzwischen beendet.', 'Wähl eine andere Sammlung oder leg eine neue an.'),
  // Jeder Block steht in einer eigenen Transaktion, und ein neuer Lauf legt
  // nichts doppelt an (ON CONFLICT DO NOTHING; ADR-0065 Nachtrag 2b).
  lease_expired: msg(
    FILL_FAILED,
    'Die Übernahme wurde unterbrochen.',
    'Starte sie noch einmal. Was schon übernommen war, bleibt drin und wird nicht doppelt gezählt.',
    true,
  ),
});

/** attempts_exhausted, internal und jeder unbekannte Grund. */
const FILL_FAILURE_FALLBACK = msg(
  FILL_FAILED,
  'Bei der Übernahme ist ein Fehler aufgetreten.',
  'Starte sie noch einmal. Was schon übernommen war, bleibt drin. Bleibt es dabei, wende dich an die LIAISON-Administration.',
  true,
);

export function fillFailureMessage(reason: string | undefined): PlainMessage {
  return FILL_FAILURES[reason ?? ''] ?? FILL_FAILURE_FALLBACK;
}

// Kam keine Antwort, weiss der Browser beim Schreiben nicht, ob es gilt.

/** Anlegen: derselbe Name legt nichts doppelt an (gleiche Kennung nach ADR-0060, sonst Name je Person eindeutig). */
export const CREATE_UNCERTAIN = msg(
  'Ob die Sammlung angelegt wurde, ist offen.',
  'Die Antwort kam nicht an.',
  'Lege sie mit demselben Namen noch einmal an. Doppelt angelegt wird dabei nichts.',
  true,
);

/** Hinzufuegen: idempotent (ON CONFLICT DO NOTHING; der Suchauftrag mit gleicher Kennung). */
export function addUncertainMessage(angelegt?: string): PlainMessage {
  return msg(
    angelegt === undefined ? 'Ob die Kontakte hinzugefügt wurden, ist offen.' : `„${angelegt}“ ist angelegt. Ob die Kontakte hinzugefügt wurden, ist offen.`,
    'Die Antwort kam nicht an.',
    'Füge sie noch einmal hinzu. Doppelt aufgenommen wird dabei niemand.',
    true,
  );
}

/**
 * Umbenennen und Beenden nach einem Abbruch: der Dialog liest die Sammlung neu
 * und vergleicht mit der Absicht. Offen bleibt es nur, wenn auch das Lesen scheitert.
 */
export function notSavedAfterAbortMessage(was: 'umbenennen' | 'beenden'): PlainMessage {
  return msg(
    was === 'umbenennen' ? 'Dein neuer Name wurde nicht gespeichert.' : 'Die Sammlung wurde nicht beendet.',
    'Die Antwort kam nicht an.',
    'Versuche es erneut.',
    true,
  );
}

export function changeUncertainMessage(was: 'umbenennen' | 'beenden'): PlainMessage {
  return msg(
    was === 'umbenennen' ? 'Ob der neue Name gespeichert wurde, ist offen.' : 'Ob die Sammlung beendet wurde, ist offen.',
    'Die Antwort kam nicht an.',
    'Schließe den Dialog und lade die Seite neu. Dann siehst du, was gilt.',
    true,
  );
}

// Lesefehler ohne Antwort sagen, was nicht kam, statt wohin die Anfrage nicht kam.
export const COLLECTIONS_UNREACHABLE = msg(
  'LIAISON ist gerade nicht erreichbar.',
  'Die Sammlungen ließen sich nicht laden.',
  'Prüfe deine Verbindung und versuche es erneut.',
  true,
);

export const COLLECTION_UNREACHABLE = msg(
  'LIAISON ist gerade nicht erreichbar.',
  'Die Sammlung ließ sich nicht laden.',
  'Prüfe deine Verbindung und versuche es erneut.',
  true,
);

/** Angelegt, aber das Hinzufuegen danach scheiterte: der Satz sagt beides. */
export function createdButNotFilledMessage(name: string, cause: PlainMessage): PlainMessage {
  return msg(
    `Die Sammlung „${name}“ ist angelegt, die Kontakte sind aber noch nicht drin.`,
    cause.explanation,
    `Versuch es noch einmal und wähl dabei „${name}“.`,
    true,
  );
}

/**
 * Ein spaeterer Block scheiterte: Was schon drin ist, bleibt (ON CONFLICT DO
 * NOTHING am offenen Mitglied, ADR-0065 Ziffer 1), und eine Wiederholung
 * legt nichts doppelt an. Keine Zahl der Hinzugefuegten.
 */
export function partlyAddedMessage(name: string): PlainMessage {
  return msg(`Nicht alle ausgewählten Kontakte sind in „${name}“.`, 'Was schon drin ist, bleibt drin. Deine Auswahl ist erhalten.', 'Versuch es noch einmal; doppelt wird dabei nichts.', true);
}

/** Abgebrochen, nachdem schon ein Block drin war -- ohne Zahl. */
export function cancelledPartlySentence(name: string): string {
  return `Abgebrochen. Ein Teil der Auswahl ist schon in „${name}“.`;
}

export const ADD_PROGRESS_LABEL = 'Ausgewählte Kontakte werden hinzugefügt';

/** "500 von 1.240" -- gezaehlt werden die eigenen gesendeten Kennungen, nicht sichtbare Kontakte. */
export function progressText(gesendet: number, gesamt: number): string {
  return `${gesendet.toLocaleString('de-DE')} von ${gesamt.toLocaleString('de-DE')}`;
}

// --- Worte ----------------------------------------------------------------------

/** "noch leer", "1 Kontakt", "1.240 Kontakte". */
export function countLabel(count: number): string {
  if (count === 0) return 'noch leer';
  return count === 1 ? '1 Kontakt' : `${count.toLocaleString('de-DE')} Kontakte`;
}

/** "angelegt am 06.10.2026" -- die Liste ist nach Anlage sortiert, nicht nach Aenderung. */
export function createdLabel(iso: string): string {
  return `angelegt am ${new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
}

/**
 * Was hinzukommt, als ein Satz ohne Wahl -- wie beim Export: die ausgewaehlten
 * Zeilen oder alle Treffer (`zahl` in Worten aus derselben Quelle wie dort).
 */
export function addScopeSentence(markiert: number, zahl: string): string {
  if (markiert > 0) {
    return markiert === 1 ? 'Hinzugefügt wird der ausgewählte Kontakt.' : `Hinzugefügt werden die ${markiert.toLocaleString('de-DE')} ausgewählten Kontakte.`;
  }
  return `Hinzugefügt werden alle Treffer dieser Suche${zahl.length > 0 ? ` (${zahl})` : ''}.`;
}

export const STATIC_HINT = 'Die Sammlung hält die Kontakte von jetzt fest. Spätere Änderungen der Suche wirken nicht nach.';

/** Nach dem Hinzufuegen: die sichtbare Gesamtzahl, nie die Zahl der Hinzugefuegten. */
export function totalSentence(name: string, count: number): string {
  const worte = count === 0 ? 'keine Kontakte' : count === 1 ? '1 Kontakt' : `${count.toLocaleString('de-DE')} Kontakte`;
  return `In „${name}“ ${count === 1 ? 'ist' : 'sind'} jetzt ${worte}.`;
}

/**
 * Abgebrochen, aber die Anlage kam noch an: ehrlich sagen, dass
 * es sie gibt, und was danach NICHT geschah.
 */
export function createdAfterCancelSentence(name: string, wo: 'uebersicht' | 'kontakte'): string {
  const satz = `Die Sammlung „${name}“ wurde noch angelegt, bevor du abgebrochen hast.`;
  return wo === 'uebersicht' ? `${satz} Sie steht jetzt in der Liste.` : `${satz} Hinzugefügt wurde nichts.`;
}

export function fillRunningSentence(name: string): string {
  return `Die Treffer werden in „${name}“ übernommen … Das läuft weiter, auch wenn du die Seite verlässt.`;
}

export function collectionHref(collectionId: string): string {
  return `/sammlungen/${encodeURIComponent(collectionId)}`;
}

// --- Abrufe ---------------------------------------------------------------------

interface CallOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}

const KINDS: readonly string[] = ['person', 'organization'];
const FILL_STATUSES: readonly string[] = ['queued', 'running', 'succeeded', 'failed'];

const UNVOLLSTAENDIG = msg(
  'Die Sammlungen lassen sich nicht anzeigen.',
  'LIAISON hat geantwortet, aber die Antwort war unvollständig.',
  'Lade die Seite neu. Bleibt es dabei, wende dich an die LIAISON-Administration.',
  true,
);

export function summaryOf(value: unknown): CollectionSummary | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.collectionId !== 'string' ||
    typeof row.collectionName !== 'string' ||
    typeof row.createdAt !== 'string' ||
    typeof row.lockVersion !== 'number' ||
    (row.memberCount !== undefined && typeof row.memberCount !== 'number')
  ) {
    return undefined;
  }
  return {
    collectionId: row.collectionId,
    collectionName: row.collectionName,
    createdAt: row.createdAt,
    lockVersion: row.lockVersion,
    memberCount: typeof row.memberCount === 'number' ? row.memberCount : undefined,
  };
}

function memberOf(value: unknown): CollectionMember | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (typeof row.partyId !== 'string' || typeof row.kind !== 'string' || !KINDS.includes(row.kind) || typeof row.lifecycleStatus !== 'string' || typeof row.displayName !== 'string') {
    return undefined;
  }
  return { partyId: row.partyId, kind: row.kind, lifecycleStatus: row.lifecycleStatus, displayName: row.displayName };
}

export function fillOf(value: unknown): FillSummary | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (typeof row.fillId !== 'string' || typeof row.collectionId !== 'string' || typeof row.status !== 'string' || !FILL_STATUSES.includes(row.status)) {
    return undefined;
  }
  return {
    fillId: row.fillId,
    collectionId: row.collectionId,
    status: row.status as FillStatus,
    failureReason: typeof row.failureReason === 'string' ? row.failureReason : undefined,
    memberCount: typeof row.memberCount === 'number' ? row.memberCount : undefined,
  };
}

const collectionsUrl = (baseUrl: string): string => `${baseUrl}/party-collections`;
const collectionUrl = (baseUrl: string, collectionId: string): string => `${collectionsUrl(baseUrl)}/${encodeURIComponent(collectionId)}`;
const withSend = (options: CallOptions): { readonly send?: SendLike } => (options.send === undefined ? {} : { send: options.send });

export type ListCollectionsOutcome =
  | { readonly outcome: 'loaded'; readonly collections: readonly CollectionSummary[]; readonly nextCursor: string | undefined }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/** Eine Seite der eigenen offenen Sammlungen, neueste zuerst, OHNE Zahl. */
export async function fetchCollections(options: CallOptions & { readonly cursor?: string | undefined }): Promise<ListCollectionsOutcome> {
  const query = options.cursor === undefined ? '' : `?cursor=${encodeURIComponent(options.cursor)}`;
  const read = await readResource({ url: `${collectionsUrl(options.baseUrl)}${query}`, accessToken: options.accessToken, ...withSend(options) });
  if (read.outcome === 'not-found') return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  if (read.outcome === 'rejected') return read;
  const items = Array.isArray(read.body.collections) ? read.body.collections.map(summaryOf) : [undefined];
  if (items.includes(undefined)) return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  const nextCursor = typeof read.body.nextCursor === 'string' ? read.body.nextCursor : undefined;
  return { outcome: 'loaded', collections: items as CollectionSummary[], nextCursor };
}

/**
 * Alle Seiten nacheinander, ohne Deckel: die Seiten sind die Grenze des
 * Antwortrahmens, keine fachliche. Jede Seite braucht den Cursor der vorigen.
 */
async function allPages<T>(
  gilt: () => boolean,
  page: (cursor: string | undefined) => Promise<{ readonly outcome: 'loaded'; readonly collections: readonly T[]; readonly nextCursor: string | undefined } | { readonly outcome: 'rejected'; readonly message: PlainMessage }>,
): Promise<{ readonly outcome: 'loaded'; readonly collections: readonly T[]; readonly nextCursor: undefined } | { readonly outcome: 'rejected'; readonly message: PlainMessage }> {
  const all: T[] = [];
  const gesehen = new Set<string>();
  let cursor: string | undefined;
  do {
    // Ein ueberholter Lauf holt keine weitere Seite.
    if (!gilt()) return { outcome: 'rejected', message: UNVOLLSTAENDIG };
    const result = await page(cursor);
    if (result.outcome !== 'loaded') return result;
    all.push(...result.collections);
    cursor = result.nextCursor;
    // Ein wiederholter Cursor waere eine Endlosschleife im Tab, keine weitere Seite.
    if (cursor !== undefined && gesehen.has(cursor)) return { outcome: 'rejected', message: UNVOLLSTAENDIG };
    if (cursor !== undefined) gesehen.add(cursor);
  } while (cursor !== undefined);
  return { outcome: 'loaded', collections: all, nextCursor: undefined };
}

/** Alle Seiten, ohne Deckel -- fuer die Wahl im Dialog. */
export function fetchAllCollections(options: CallOptions): Promise<ListCollectionsOutcome> {
  return allPages(() => true, (cursor) => fetchCollections({ ...options, cursor }));
}

// --- "In der Sammlung" im Kontaktkopf --------------------------------------------

/** Eine eigene offene Sammlung, in der ein Kontakt steht: nur Kennung und Name. */
export interface CollectionMention {
  readonly collectionId: string;
  readonly collectionName: string;
}

export type PartyCollectionsOutcome =
  | { readonly outcome: 'loaded'; readonly collections: readonly CollectionMention[]; readonly nextCursor: string | undefined }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

function mentionOf(value: unknown): CollectionMention | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const row = value as Record<string, unknown>;
  return typeof row.collectionId === 'string' && typeof row.collectionName === 'string'
    ? { collectionId: row.collectionId, collectionName: row.collectionName }
    : undefined;
}

/** Eine Seite der eigenen offenen Sammlungen mit diesem Kontakt, nach Anlage geordnet. */
export async function fetchPartyCollections(
  options: CallOptions & { readonly partyId: string; readonly cursor?: string | undefined },
): Promise<PartyCollectionsOutcome> {
  const query = options.cursor === undefined ? '' : `?cursor=${encodeURIComponent(options.cursor)}`;
  const url = `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/collections${query}`;
  const read = await readResource({ url, accessToken: options.accessToken, ...withSend(options) });
  if (read.outcome === 'not-found') return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  if (read.outcome === 'rejected') return read;
  const items = Array.isArray(read.body.collections) ? read.body.collections.map(mentionOf) : [undefined];
  if (items.includes(undefined)) return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  const nextCursor = typeof read.body.nextCursor === 'string' ? read.body.nextCursor : undefined;
  return { outcome: 'loaded', collections: items as CollectionMention[], nextCursor };
}

/**
 * Alle Seiten, erst dann eine Zahl: eine Zahl aus der ersten Seite waere ab 51
 * Sammlungen falsch. Geordnet nach dem Namen fuer das Auge (Collator 'de'),
 * nicht nach der Anlage, in der der Server blaettert.
 */
export async function fetchAllPartyCollections(
  options: CallOptions & { readonly partyId: string; /** Falsch, sobald ein neuerer Lauf begonnen hat. */ readonly gilt?: () => boolean },
): Promise<PartyCollectionsOutcome> {
  const result = await allPages(options.gilt ?? (() => true), (cursor) => fetchPartyCollections({ ...options, cursor }));
  if (result.outcome !== 'loaded') return result;
  const collator = new Intl.Collator('de');
  return { ...result, collections: [...result.collections].sort((a, b) => collator.compare(a.collectionName, b.collectionName)) };
}

/** Der Hinweis im Kopf; bei 0 steht keiner. */
export function inCollectionsLabel(count: number): string {
  return count === 1 ? 'In 1 Sammlung' : `In ${count.toLocaleString('de-DE')} Sammlungen`;
}

export type ReadCollectionOutcome =
  | { readonly outcome: 'loaded'; readonly collection: CollectionSummary & { readonly memberCount: number } }
  | { readonly outcome: 'gone' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/** Eine Sammlung mit sichtbarer Zahl. */
export async function fetchCollection(options: CallOptions & { readonly collectionId: string }): Promise<ReadCollectionOutcome> {
  const read = await readResource({ url: collectionUrl(options.baseUrl, options.collectionId), accessToken: options.accessToken, ...withSend(options) });
  if (read.outcome === 'not-found') return { outcome: 'gone' };
  if (read.outcome === 'rejected') return read;
  const collection = summaryOf(read.body);
  if (collection?.memberCount === undefined) return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  return { outcome: 'loaded', collection: collection as CollectionSummary & { readonly memberCount: number } };
}

export type ReadMembersOutcome =
  | {
      readonly outcome: 'loaded';
      readonly collection: CollectionSummary & { readonly memberCount: number };
      readonly members: readonly CollectionMember[];
      readonly nextCursor: string | undefined;
    }
  | { readonly outcome: 'gone' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/** Eine Seite sichtbarer Mitglieder samt der Sammlung und ihrer Zahl. */
export async function fetchMembers(options: CallOptions & { readonly collectionId: string; readonly cursor?: string | undefined }): Promise<ReadMembersOutcome> {
  const query = options.cursor === undefined ? '' : `?cursor=${encodeURIComponent(options.cursor)}`;
  const read = await readResource({ url: `${collectionUrl(options.baseUrl, options.collectionId)}/members${query}`, accessToken: options.accessToken, ...withSend(options) });
  if (read.outcome === 'not-found') return { outcome: 'gone' };
  if (read.outcome === 'rejected') return read;
  const collection = summaryOf(read.body);
  const members = Array.isArray(read.body.parties) ? read.body.parties.map(memberOf) : [undefined];
  if (collection?.memberCount === undefined || members.includes(undefined)) return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  return {
    outcome: 'loaded',
    collection: collection as CollectionSummary & { readonly memberCount: number },
    members: members as CollectionMember[],
    nextCursor: typeof read.body.nextCursor === 'string' ? read.body.nextCursor : undefined,
  };
}

export type CollectionCommandOutcome = CommandOutcome<{ readonly collection: CollectionSummary | undefined }>;

export function createCollection(options: CallOptions & { readonly name: string; readonly clientRequestId: string }): Promise<CollectionCommandOutcome> {
  return sendCommand({
    url: collectionsUrl(options.baseUrl),
    accessToken: options.accessToken,
    ...withSend(options),
    body: { name: options.name.normalize('NFC').trim(), clientRequestId: options.clientRequestId },
    messages: MESSAGES,
    applied: (record) => ({ collection: summaryOf(record) }),
  });
}

/** Hoechstens 250 Kennungen je Aufruf (technische Blockgroesse der Route); die Antwort nennt die neue sichtbare Gesamtzahl. */
export function addMembers(options: CallOptions & { readonly collectionId: string; readonly partyIds: readonly string[] }): Promise<CollectionCommandOutcome> {
  return sendCommand({
    url: `${collectionUrl(options.baseUrl, options.collectionId)}/members`,
    accessToken: options.accessToken,
    ...withSend(options),
    body: { partyIds: [...options.partyIds] },
    messages: MESSAGES,
    applied: (record) => ({ collection: summaryOf(record) }),
  });
}

/** Umbenennen mit dem gelesenen Stand; ein veralteter Stand ist 409 `party_collection_outdated`. */
export function renameCollection(
  options: CallOptions & { readonly collectionId: string; readonly name: string; readonly lockVersion: number },
): Promise<CollectionCommandOutcome> {
  return sendCommand({
    url: `${collectionUrl(options.baseUrl, options.collectionId)}/rename`,
    accessToken: options.accessToken,
    ...withSend(options),
    body: { name: options.name.normalize('NFC').trim(), lockVersion: options.lockVersion },
    messages: { ...MESSAGES, conflicts: { ...REASON_MESSAGES, party_collection_outdated: outdatedMessage('umbenennen') } },
    applied: (record) => ({ collection: summaryOf(record) }),
  });
}

/** Beenden ist endgueltig (ADR-0065 Ziffer 2); die Antwort traegt keine Sammlung mehr. */
export function endCollection(
  options: CallOptions & { readonly collectionId: string; readonly lockVersion: number },
): Promise<CommandOutcome<Record<never, never>>> {
  return sendCommand({
    url: `${collectionUrl(options.baseUrl, options.collectionId)}/end`,
    accessToken: options.accessToken,
    ...withSend(options),
    body: { lockVersion: options.lockVersion },
    messages: { ...MESSAGES, conflicts: { ...REASON_MESSAGES, party_collection_outdated: outdatedMessage('beenden') } },
    applied: () => ({}),
  });
}

/** Hoechstens 250 Kennungen je Aufruf; die Antwort nennt die neue sichtbare Gesamtzahl. */
export function removeMembers(options: CallOptions & { readonly collectionId: string; readonly partyIds: readonly string[] }): Promise<CollectionCommandOutcome> {
  return sendCommand({
    url: `${collectionUrl(options.baseUrl, options.collectionId)}/members/remove`,
    accessToken: options.accessToken,
    ...withSend(options),
    body: { partyIds: [...options.partyIds] },
    messages: MESSAGES,
    applied: (record) => ({ collection: summaryOf(record) }),
  });
}

/** Zerlegt in Bloecke der Mitgliederroute, in der Reihenfolge der Auswahl. */
export function blocksOf<T>(items: readonly T[], size: number = MEMBER_BLOCK_SIZE): readonly (readonly T[])[] {
  const bloecke: T[][] = [];
  for (let ab = 0; ab < items.length; ab += size) bloecke.push(items.slice(ab, ab + size));
  return bloecke;
}

export type AddInBlocksOutcome =
  /** Alle Bloecke drin; die Zahl ist die der letzten Antwort. */
  | { readonly outcome: 'fertig'; readonly collection: CollectionSummary & { readonly memberCount: number } }
  /** Vor dem naechsten Block abgebrochen; `teilweise`, wenn schon einer drin ist. */
  | { readonly outcome: 'abgebrochen'; readonly teilweise: boolean }
  /** Ein Block scheiterte; `teilweise`, wenn vorher schon einer drin war; `weg`, wenn die Sammlung nicht mehr da ist (404). */
  | { readonly outcome: 'gescheitert'; readonly teilweise: boolean; readonly message: PlainMessage; readonly weg?: boolean };

/**
 * Fuegt die Kennungen in Bloecken zu 250 NACHEINANDER hinzu. Vor jedem Block
 * fragt `abgebrochen`, ob die Nutzerin aufgehoert hat; `fortschritt` bekommt
 * die Zahl der gesendeten Kennungen nach jedem Block.
 */
export function addMembersInBlocks(options: BlockOptions): Promise<AddInBlocksOutcome> {
  return inBlocks(options, addMembers);
}

/** Entfernt in Bloecken zu 250, nacheinander; dieselben Ausgaenge wie das Hinzufuegen. */
export function removeMembersInBlocks(options: BlockOptions): Promise<AddInBlocksOutcome> {
  return inBlocks(options, removeMembers);
}

type BlockOptions = CallOptions & {
  readonly collectionId: string;
  readonly partyIds: readonly string[];
  readonly abgebrochen?: () => boolean;
  readonly fortschritt?: (gesendet: number) => void;
};

async function inBlocks(
  options: BlockOptions,
  senden: (options: CallOptions & { readonly collectionId: string; readonly partyIds: readonly string[] }) => Promise<CollectionCommandOutcome>,
): Promise<AddInBlocksOutcome> {
  let gesendet = 0;
  let letzte: (CollectionSummary & { readonly memberCount: number }) | undefined;
  for (const block of blocksOf(options.partyIds)) {
    if (options.abgebrochen?.() === true) return { outcome: 'abgebrochen', teilweise: gesendet > 0 };
    const result = await senden({ ...options, partyIds: block });
    if (result.outcome !== 'applied' || result.collection?.memberCount === undefined) {
      return {
        outcome: 'gescheitert',
        teilweise: gesendet > 0,
        message: 'message' in result ? result.message : UNVOLLSTAENDIG,
        ...(result.outcome === 'not-found' ? { weg: true } : {}),
      };
    }
    gesendet += block.length;
    letzte = result.collection as CollectionSummary & { readonly memberCount: number };
    options.fortschritt?.(gesendet);
  }
  return letzte === undefined ? { outcome: 'gescheitert', teilweise: false, message: UNVOLLSTAENDIG } : { outcome: 'fertig', collection: letzte };
}

export type FillCommandOutcome = CommandOutcome<{ readonly fill: FillSummary | undefined }>;

/** Alle Treffer einer Suche als Auftrag (202); dieselben Rumpfteile wie Suche und Export. */
export function createFill(
  options: CallOptions & {
    readonly collectionId: string;
    readonly term?: string | undefined;
    readonly filter?: SearchFilter | undefined;
    readonly clientRequestId: string;
  },
): Promise<FillCommandOutcome> {
  const filter = options.filter === undefined ? undefined : filterBody(options.filter);
  return sendCommand({
    url: `${collectionUrl(options.baseUrl, options.collectionId)}/fills`,
    accessToken: options.accessToken,
    ...withSend(options),
    body: {
      ...(options.term === undefined ? {} : { term: options.term }),
      ...(filter === undefined || Object.keys(filter).length === 0 ? {} : { filter }),
      clientRequestId: options.clientRequestId,
    },
    messages: MESSAGES,
    applied: (record) => ({ fill: fillOf(record) }),
  });
}

export type ReadFillOutcome =
  | { readonly outcome: 'loaded'; readonly fill: FillSummary }
  | { readonly outcome: 'gone' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export async function fetchFill(options: CallOptions & { readonly collectionId: string; readonly fillId: string }): Promise<ReadFillOutcome> {
  const read = await readResource({
    url: `${collectionUrl(options.baseUrl, options.collectionId)}/fills/${encodeURIComponent(options.fillId)}`,
    accessToken: options.accessToken,
    ...withSend(options),
  });
  if (read.outcome === 'not-found') return { outcome: 'gone' };
  if (read.outcome === 'rejected') return read;
  const fill = fillOf(read.body);
  return fill === undefined ? { outcome: 'rejected', message: UNVOLLSTAENDIG } : { outcome: 'loaded', fill };
}
